import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { OceanBackground } from '../../components/OceanBackground';
import './AuthPage.css';

type AuthView = 'login' | 'register' | 'forgot' | 'check-email' | 'reset-confirm' | 'link-tg';

interface AuthPageProps {
  onAuthenticated: () => void;
}

export function AuthPage({ onAuthenticated }: AuthPageProps) {
  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [message, setMessage] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [linkCode, setLinkCode] = useState('');

  const auth = useAuth();

  // Check URL for reset token on mount
  useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t && window.location.pathname === '/reset-password') {
      setResetToken(t);
      setView('reset-confirm');
    }
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await auth.login(email, password);
    if (ok) onAuthenticated();
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = await auth.register(email, password, firstName);
    if (msg === '__authenticated__') {
      onAuthenticated();
    } else if (msg) {
      setMessage(msg);
      setView('check-email');
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = await auth.resetPassword(email);
    if (msg) {
      setMessage(msg);
      setView('check-email');
    }
  };

  const handleResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = await auth.confirmResetPassword(resetToken, password);
    if (msg) {
      setMessage(msg);
      setView('login');
      auth.setError(null);
    }
  };

  return (
    <div className="auth-page">
      <OceanBackground />

      <motion.div
        className="auth-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="auth-logo">
          <img src="/logo-full.png?v=3" alt="PATRiK VPN" className="auth-logo-full" />
        </div>

        <AnimatePresence mode="wait">
          {view === 'login' && (
            <motion.form
              key="login"
              className="auth-form"
              onSubmit={handleLogin}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <h2>Вход</h2>
              {auth.error && <div className="auth-error">{auth.error}</div>}
              {message && <div className="auth-success">{message}</div>}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="current-password"
              />
              <button type="submit" className="auth-btn-primary" disabled={auth.loading}>
                {auth.loading ? 'Входим...' : 'Войти'}
              </button>
              <div className="auth-links">
                <button type="button" onClick={() => { auth.setError(null); setView('register'); }}>
                  Создать аккаунт
                </button>
                <button type="button" onClick={() => { auth.setError(null); setView('forgot'); }}>
                  Забыли пароль?
                </button>
              </div>
              <div className="auth-divider"><span>или</span></div>
              <button type="button" className="auth-btn-tg" onClick={() => { auth.setError(null); setView('link-tg'); }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M9.78 17.5l.38-3.59 6.53-5.89c.28-.25-.06-.37-.43-.14L5.77 14.04 2.27 12.9c-.75-.23-.76-.75.16-1.11l13.15-5.07c.62-.24 1.16.15.96.79l-2.25 10.6c-.16.75-.62.93-1.26.58L9.78 17.5z" fill="currentColor"/>
                </svg>
                Войти через Telegram
              </button>
            </motion.form>
          )}

          {view === 'register' && (
            <motion.form
              key="register"
              className="auth-form"
              onSubmit={handleRegister}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2>Регистрация</h2>
              {auth.error && <div className="auth-error">{auth.error}</div>}
              <input
                type="text"
                placeholder="Имя"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Пароль (мин. 6 символов)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button type="submit" className="auth-btn-primary" disabled={auth.loading}>
                {auth.loading ? 'Создаём...' : 'Создать аккаунт'}
              </button>
              <div className="auth-links">
                <button type="button" onClick={() => { auth.setError(null); setView('login'); }}>
                  Уже есть аккаунт? Войти
                </button>
              </div>
            </motion.form>
          )}

          {view === 'forgot' && (
            <motion.form
              key="forgot"
              className="auth-form"
              onSubmit={handleForgot}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2>Восстановление пароля</h2>
              {auth.error && <div className="auth-error">{auth.error}</div>}
              <p className="auth-desc">Введите email и мы отправим ссылку для сброса пароля.</p>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <button type="submit" className="auth-btn-primary" disabled={auth.loading}>
                {auth.loading ? 'Отправляем...' : 'Отправить ссылку'}
              </button>
              <div className="auth-links">
                <button type="button" onClick={() => { auth.setError(null); setView('login'); }}>
                  Назад ко входу
                </button>
              </div>
            </motion.form>
          )}

          {view === 'check-email' && (
            <motion.div
              key="check-email"
              className="auth-form auth-message-view"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="auth-icon-check">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#2E9BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>Проверьте почту</h2>
              <p className="auth-desc">{message}</p>
              <button
                type="button"
                className="auth-btn-primary"
                onClick={() => { setMessage(''); setView('login'); }}
              >
                Перейти ко входу
              </button>
            </motion.div>
          )}

          {view === 'reset-confirm' && (
            <motion.form
              key="reset-confirm"
              className="auth-form"
              onSubmit={handleResetConfirm}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>Новый пароль</h2>
              {auth.error && <div className="auth-error">{auth.error}</div>}
              <input
                type="password"
                placeholder="Новый пароль (мин. 6 символов)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button type="submit" className="auth-btn-primary" disabled={auth.loading}>
                {auth.loading ? 'Сохраняем...' : 'Сохранить пароль'}
              </button>
            </motion.form>
          )}

          {view === 'link-tg' && (
            <motion.div
              key="link-tg"
              className="auth-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2>Войти через Telegram</h2>
              {auth.error && <div className="auth-error">{auth.error}</div>}
              {message && <div className="auth-success">{message}</div>}
              <p className="auth-desc">
                1. Откройте бота <a href="https://t.me/my_patrick_vpn_bot" target="_blank" rel="noreferrer">@my_patrick_vpn_bot</a><br />
                2. Отправьте боту команду <b>/link</b><br />
                3. Введите полученный 6-значный код
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (auth.isAuthenticated) {
                  // Already logged in — link existing web account to TG
                  const ok = await auth.linkTelegram(linkCode);
                  if (ok) {
                    setMessage('Аккаунт связан с Telegram!');
                    setLinkCode('');
                    setTimeout(() => onAuthenticated(), 1500);
                  }
                } else {
                  // Not logged in — login directly via TG code
                  const ok = await auth.loginTelegram(linkCode);
                  if (ok) {
                    setMessage('Вход выполнен!');
                    setLinkCode('');
                    setTimeout(() => onAuthenticated(), 1000);
                  }
                }
              }}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="6-значный код"
                  value={linkCode}
                  onChange={e => setLinkCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  style={{textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3em'}}
                />
                <button type="submit" className="auth-btn-primary" disabled={auth.loading || linkCode.length !== 6}>
                  {auth.loading ? 'Входим...' : 'Войти'}
                </button>
              </form>
              <div className="auth-links">
                <button type="button" onClick={() => { auth.setError(null); setMessage(''); setView('login'); }}>
                  Войти по email
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
